import React, { useCallback, useEffect, useRef, useState } from "react";
import api from "../api/client";
import ConsoleInvitePanel from "./ConsoleInvitePanel";

/* Native screen-share viewer — attended remote view + control, no vendor/VPN.
 * Opens a session, streams the device's live desktop (JPEG frames) onto a
 * canvas, and (in control mode) relays mouse/keyboard back over the WebSocket.
 * Full-screen overlay opened from the device page. */

// Special-key -> Windows virtual-key codes (browser keyCode aligns for these).
const SPECIAL_VK: Record<string, number> = {
  Backspace: 8, Tab: 9, Enter: 13, Shift: 16, Control: 17, Alt: 18, Escape: 27,
  " ": 32, PageUp: 33, PageDown: 34, End: 35, Home: 36,
  ArrowLeft: 37, ArrowUp: 38, ArrowRight: 39, ArrowDown: 40,
  Delete: 46, Meta: 91,
};

export default function ScreenShareView({
  deviceId,
  hostname,
  onClose,
  token: presetToken,
  observeToken,
}: {
  deviceId: number;
  hostname: string;
  onClose: () => void;
  token?: string;
  /** WATCH an existing session instead of opening one.
   *
   *  Reuses this component rather than adding a second viewer: frame
   *  decoding, canvas sizing and the reconnect handling are the fiddly
   *  parts, and two copies means the watcher's view quietly drifts from the
   *  operator's. In this mode the socket is the read-only observe endpoint,
   *  so there is nothing to send back even if the UI tried. */
  observeToken?: string;
}) {
  const observing = !!observeToken;
  /** The token of the session actually on screen, so the toolbar can offer
   *  to invite somebody into THIS one. A reconnect mints a new session, so
   *  this has to be state rather than the local the connect closure used. */
  const [activeToken, setActiveToken] = useState<string | null>(observeToken || null);
  // The same value as a ref. ws.onclose is a closure created when the
  // socket opened, so it would read whatever activeToken was AT THAT
  // MOMENT - which on a reconnect is the previous session's token, and the
  // diagnosis would be fetched for the wrong one.
  const activeTokenRef = useRef<string | null>(observeToken || null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const [status, setStatus] = useState<"connecting" | "live" | "closed" | "error">("connecting");
  const [statusMsg, setStatusMsg] = useState("Requesting session…");
  // A watcher is never in control. Defaulting to true and relying on the
  // socket to ignore input would still show them a control-mode toolbar.
  const [control, setControl] = useState(!observeToken);
  const controlRef = useRef(!observeToken);
  const [fps, setFps] = useState(0);
  const frameCount = useRef(0);
  const lastFrameRef = useRef<number>(Date.now());
  const wasLiveRef = useRef(false);
  /**
   * Has the CURRENT socket delivered a frame, and when was it opened?
   *
   * Both exist because `wasLiveRef` is per-component and is set once, on the
   * first frame ever. That made the staleness watchdog below apply to every
   * later socket from the moment it opened - including one that was simply
   * waiting for the agent to notice its session. The agent polls for
   * commands every 20s when idle and was measured taking 30s, then 70s; the
   * watchdog gave up at 14s. So one real drop became five sessions in a
   * minute, four of them closed the instant the agent finally arrived and
   * the broker kept the newest.
   */
  const liveOnThisSocketRef = useRef(false);
  const socketOpenedAtRef = useRef<number>(Date.now());
  const errRef = useRef<string | null>(null);
  const [monitors, setMonitors] = useState<{ i: number; w: number; h: number }[]>([]);
  const [monCount, setMonCount] = useState(0);
  const [selMon, setSelMon] = useState(1);
  /* No-frames watchdog.
   *
   * REPORTED: "console connect never loads" - the operator watched a spinner
   * indefinitely. Root cause on that machine was a session with no desktop
   * behind it (an Azure DC with nobody signed in), but the operator-facing
   * bug is the same whatever the cause: the socket connects, no frame ever
   * arrives, and nothing says so. Anything that produces no first frame ends
   * up here, so this covers causes nobody has hit yet.
   */
  const [stalled, setStalled] = useState(false);
  /* Clipboard bridge.
   *
   * REPORTED (1): "console sessions - no ability to have clipboard."
   * REPORTED (2): "seems to be working on send to machine but not get from
   * machine. Would be nice just to have it seamless automatically and
   * bidirectional."
   *
   * (2) was two separate things. The BUG was in the agent: ctypes returned
   * GetClipboardData's 64-bit handle through a 32-bit int, so every read
   * truncated and reported an empty clipboard - see the restype block in
   * screenshare.py. The WISH is this: sync runs on its own now.
   *
   * HOW EACH DIRECTION MANAGES IT, because they are not symmetric:
   *
   *   remote -> here : the agent watches the remote clipboard's change
   *     counter and pushes ("auto":true). Chromium grants clipboard-WRITE
   *     to a focused document with no prompt, so this needs nothing from
   *     the operator. If the page is not focused the write is refused, so
   *     the text is held and retried the moment it is.
   *
   *   here -> remote : reading the LOCAL clipboard needs a permission the
   *     browser may not have yet. We try; if it is refused we say so and
   *     offer one click to grant it, because a prompt can only be raised
   *     from a real gesture. Ctrl+V keeps working either way - it IS a
   *     gesture.
   *
   * The panel stays. It is the part that cannot be refused by anything, it
   * is where a blocked permission gets explained, and it is the only place
   * to SEE what came across.
   */
  const [clipOpen, setClipOpen] = useState(false);
  const [clipText, setClipText] = useState("");
  const [clipNote, setClipNote] = useState("");
  /** "on" once a local read has actually succeeded; "blocked" once one has
   *  been refused. Held in a ref as well so the sync loop can check it
   *  without listing it as a dependency and rebuilding its timer. */
  const [autoClip, setAutoClip] = useState<"probing" | "on" | "blocked">("probing");
  const autoClipRef = useRef(autoClip);
  autoClipRef.current = autoClip;
  /** The last text known to be on THIS machine's clipboard, whether we read
   *  it or wrote it. Both directions check it: it is what stops a value
   *  arriving from the remote machine being pushed straight back. */
  const lastLocalClipRef = useRef<string | null>(null);
  /** Text the remote machine sent that the browser would not let us write
   *  yet (the page was not focused). Retried on focus. */
  const heldRemoteClipRef = useRef<string | null>(null);

  useEffect(() => {
    controlRef.current = control;
  }, [control]);

  // 35s: comfortably past a slow agent poll (the runner picks work up within
  // ~20s) but well inside a technician's patience.
  useEffect(() => {
    if (status === "live" || status === "error") {
      setStalled(false);
      return;
    }
    const id = setTimeout(() => setStalled(true), 35000);
    return () => clearTimeout(id);
  }, [status]);

  // Both read the socket through a ref, so neither has any real dependency
  // and both are useCallback([]) — stable identities the effects below can
  // honestly declare instead of suppressing the lint rule.
  const send = useCallback((obj: any) => {
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify(obj));
  }, []);

  // Send a key combo as press-all-down then release-in-reverse (so e.g.
  // Ctrl+Shift+Esc registers as a chord). VK codes.
  // The socket effect below names the device in its status messages. It must
  // NOT depend on the prop: re-running that effect drops and rebuilds the
  // screen-share connection, and a display name changing is no reason to
  // interrupt somebody's session.
  const hostnameRef = useRef(hostname);
  hostnameRef.current = hostname;

  // Same treatment, same reason. The socket effect must not list this prop:
  // re-running it drops and rebuilds a live connection, and the file already
  // learned that lesson with `hostname` above. It is fixed for the life of
  // the page anyway - it comes from the URL.
  const observeTokenRef = useRef(observeToken);
  observeTokenRef.current = observeToken;

  const combo = useCallback(
    (vks: number[]) => {
      vks.forEach((vk) => send({ t: "k", vk, a: "down" }));
      [...vks].reverse().forEach((vk) => send({ t: "k", vk, a: "up" }));
    },
    [send],
  );
  const MACROS: { label: string; run: () => void; title: string }[] = [
    { label: "Ctrl+Alt+Del", title: "Secure Attention Sequence (works on the lock/login screen)", run: () => send({ t: "sas" }) },
    { label: "Task Mgr", title: "Ctrl+Shift+Esc", run: () => combo([17, 16, 27]) },
    { label: "Alt+Tab", title: "Switch window", run: () => combo([18, 9]) },
    { label: "Alt+F4", title: "Close window", run: () => combo([18, 115]) },
    { label: "⊞ Win", title: "Windows key", run: () => combo([91]) },
  ];

  // ── clipboard bridge ──────────────────────────────────────────────────
  const fetchRemoteClipboard = () => {
    setClipNote("Reading…");
    send({ t: "clipget" });
  };

  /** Put text the remote machine copied onto THIS machine's clipboard. */
  const adoptRemoteClip = useCallback(async (text: string) => {
    // Record it as the local clipboard's contents BEFORE attempting the
    // write. If the write is refused the operator may still copy it by
    // hand, and either way the local->remote push must not treat this as
    // something new to send back.
    lastLocalClipRef.current = text;
    try {
      await navigator.clipboard.writeText(text);
      heldRemoteClipRef.current = null;
      setClipNote(`Copied ${text.length} characters from ${hostnameRef.current}.`);
    } catch {
      // Chromium refuses writeText when the document is not focused, which
      // is precisely when it matters: the operator has alt-tabbed away to
      // paste somewhere. Hold it and take the next focus.
      heldRemoteClipRef.current = text;
      setClipNote(
        `${hostnameRef.current} copied something — click the console once and it will land on your clipboard.`,
      );
    }
  }, []);

  /** Send THIS machine's clipboard to the remote one if it has changed.
   *  Returns false if the browser refused to let us read it. */
  const pushLocalClip = useCallback(async (): Promise<boolean> => {
    if (!controlRef.current) return true;
    let text = "";
    try {
      text = await navigator.clipboard.readText();
    } catch {
      setAutoClip("blocked");
      return false;
    }
    setAutoClip("on");
    if (text === lastLocalClipRef.current) return true; // nothing new
    lastLocalClipRef.current = text;
    if (text) send({ t: "clipset", text });
    return true;
  }, [send]);

  /* The sync loop.
   *
   * Polls rather than listening because there is no event for "the clipboard
   * changed" available to a page - `copy` only fires for copies made INSIDE
   * the document, and this view forwards every keystroke to the remote
   * machine instead. document.hasFocus() keeps it from reading the clipboard
   * of a window the operator is not actually looking at.
   *
   * 1.5s: fast enough that copy-then-switch-and-paste feels immediate,
   * slow enough to be invisible. */
  useEffect(() => {
    if (status !== "live" || !control) return;
    const CLIP_POLL_MS = 1500;
    const tick = () => {
      if (autoClipRef.current === "blocked") return; // refused; wait to be asked
      if (!document.hasFocus()) return;
      void pushLocalClip();
    };
    const onFocus = () => {
      // Focus is the moment both halves can catch up: anything the remote
      // machine sent while we were away can finally be written, and the
      // operator may have copied something in another window.
      const held = heldRemoteClipRef.current;
      if (held !== null) void adoptRemoteClip(held);
      tick();
    };
    window.addEventListener("focus", onFocus);
    const id = setInterval(tick, CLIP_POLL_MS);
    tick();
    return () => {
      clearInterval(id);
      window.removeEventListener("focus", onFocus);
    };
  }, [status, control, pushLocalClip, adoptRemoteClip]);

  const sendClipboardToRemote = (text: string) => {
    if (!controlRef.current) {
      setClipNote("This session is view-only, so the remote clipboard cannot be changed.");
      return false;
    }
    send({ t: "clipset", text });
    return true;
  };

  /** Copy what came back from the remote machine to THIS machine.
   *
   *  writeText can be refused (permission, or the document not being focused),
   *  and a silent failure here means the operator pastes something stale
   *  somewhere important - so it says which happened.
   */
  const copyFetchedToLocal = async () => {
    try {
      await navigator.clipboard.writeText(clipText);
      setClipNote("Copied to this computer's clipboard.");
    } catch {
      setClipNote("The browser blocked the copy. Select the text above and press Ctrl+C.");
    }
  };

  /** Push THIS machine's clipboard to the remote one, then paste it there. */
  const pasteLocalToRemote = useCallback(async () => {
    if (!controlRef.current) return;
    let text = "";
    try {
      text = await navigator.clipboard.readText();
    } catch {
      // Blocked or unsupported. Open the panel rather than doing nothing -
      // a Ctrl+V that silently no-ops is the complaint we are fixing.
      setClipOpen(true);
      setClipNote(
        "The browser would not let the page read your clipboard. Paste it into the box below and press Send.",
      );
      return;
    }
    if (!text) {
      setClipOpen(true);
      setClipNote("Your clipboard is empty, or holds something that is not text.");
      return;
    }
    send({ t: "clipset", text });
    // Give the agent a moment to own the clipboard before the paste keystroke
    // lands - otherwise Ctrl+V fires against the OLD contents.
    setTimeout(() => combo([17, 86]), 150);
  }, [send, combo]);

  const toggleFullscreen = () => {
    const el = rootRef.current;
    if (!el) return;
    if (document.fullscreenElement) document.exitFullscreen().catch(() => {});
    else el.requestFullscreen().catch(() => {});
  };

  const switchMonitor = (i: number) => {
    setSelMon(i);
    send({ t: "monitor", i });
  };

  // Map a pointer event to normalized 0..1 coords over the rendered image.
  const norm = (e: React.MouseEvent) => {
    const c = canvasRef.current!;
    const r = c.getBoundingClientRect();
    return {
      x: Math.min(1, Math.max(0, (e.clientX - r.left) / r.width)),
      y: Math.min(1, Math.max(0, (e.clientY - r.top) / r.height)),
    };
  };

  useEffect(() => {
    let cancelled = false;
    let ws: WebSocket | null = null;
    let attempt = 0;
    const MAX_ATTEMPTS = 10;
    /**
     * How long to wait for a socket that has never delivered a frame.
     *
     * Must comfortably exceed the agent's worst case: a 20s idle command
     * poll, plus spawning the worker in the console session, plus its dial
     * back. Measured 30s and 70s on a live device. The server itself waits
     * 200s before answering `agent_did_not_connect`, so anything under that
     * is the browser giving up before the server has.
     */
    const CONNECT_BUDGET_MS = 100000;
    /**
     * Backoff between reconnects. A flat 2s meant a device that could not
     * answer burned all ten attempts - and ten SESSIONS, each one an agent
     * command - inside twenty seconds.
     */
    const retryDelay = (n: number) => Math.min(20000, 2000 * 2 ** Math.max(0, n - 1));

    const connect = async (isRetry: boolean) => {
      try {
        // THIS socket has delivered nothing yet, whatever earlier ones did.
        // See the watchdog below: `wasLiveRef` is per-COMPONENT and is never
        // reset, so once any frame had ever arrived the staleness rule
        // applied to brand-new sockets that were merely still waiting for
        // the agent - and force-closed them at 14s.
        liveOnThisSocketRef.current = false;
        socketOpenedAtRef.current = Date.now();
        // First mount can reuse the token the opener minted; every reconnect
        // must mint a FRESH session (the old one is closed once its worker
        // dies — e.g. when the user signs in and the session is torn down).
        let token = !isRetry ? presetToken : undefined;
        if (observeTokenRef.current) {
          // Watching: never mint a session. A watcher who opened one would
          // be a SECOND session against the device, not a view of the one
          // they were invited to.
          token = observeTokenRef.current;
        } else if (!token) {
          const r = await api.post("/api/v1/screenshare/sessions", {
            device_id: deviceId,
            mode: "control",
          });
          token = r.data.token;
        }
        if (cancelled) return;
        setActiveToken(token || null);
        activeTokenRef.current = token || null;
        setStatus("connecting");
        setStalled(false);
        setStatusMsg(
          observeTokenRef.current
            ? "Joining the session…"
            : isRetry
              ? "Reconnecting…"
              : "Waiting for the device to connect…",
        );
        const jwt = localStorage.getItem("token") || "";
        const proto = window.location.protocol === "https:" ? "wss:" : "ws:";
        const url = observeTokenRef.current
          ? `${proto}//${window.location.host}/api/v1/console/screenshare/${token}/observe?access_token=${encodeURIComponent(jwt)}`
          : `${proto}//${window.location.host}/api/v1/screenshare/ws/${token}?access_token=${encodeURIComponent(jwt)}`;
        ws = new WebSocket(url);
        ws.binaryType = "blob";
        wsRef.current = ws;
        errRef.current = null;
        ws.onmessage = (ev) => {
          if (typeof ev.data === "string") {
            try {
              const msg = JSON.parse(ev.data);
              if (msg.t === "geo") {
                setStatusMsg("");
                if (Array.isArray(msg.monitors)) setMonitors(msg.monitors);
                if (typeof msg.count === "number") setMonCount(msg.count);
                if (typeof msg.sel === "number") setSelMon(msg.sel);
              } else if (msg.t === "clip") {
                const text: string = msg.text || "";
                setClipText(text);
                if (msg.auto && controlRef.current) {
                  // Unasked-for: the remote clipboard CHANGED. Mirror it.
                  // Gated on control for the same reason typing is - a
                  // watcher who was invited to look at a session should not
                  // have their own clipboard rewritten from under them.
                  // They can still fetch it deliberately below.
                  void adoptRemoteClip(text);
                } else if (msg.auto) {
                  setClipNote(
                    `${hostnameRef.current} copied ${text.length} characters — open Clipboard to take a copy.`,
                  );
                } else {
                  // In answer to our own clipget.
                  setClipNote(
                    text.length
                      ? `Fetched ${text.length} characters from ${hostnameRef.current}.`
                      : `${hostnameRef.current}'s clipboard is empty, or holds something that is not text.`,
                  );
                }
              } else if (msg.t === "clipack") {
                setClipNote(
                  msg.ok
                    ? `Sent. Paste on ${hostnameRef.current} with Ctrl+V.`
                    : `${hostnameRef.current} would not accept the clipboard — another program is holding it. Try again.`,
                );
              } else if (msg.t === "err") {
                const em: string = msg.msg || "The device couldn't start the screen share.";
                errRef.current = em;
                setStatus("error");
                setStatusMsg(em);
              }
            } catch {
              /* ignore */
            }
            return;
          }
          // Binary = one JPEG frame.
          const blob = ev.data as Blob;
          /**
           * Acknowledge the frame once it is DRAWN, and only then.
           *
           * This is the brake on the whole pipeline. Without it the agent
           * produces frames on a timer regardless of whether anyone is
           * keeping up, and they accumulate in the WebSocket and TCP send
           * buffers - measured at 67 frames, about eleven seconds of screen,
           * for a viewer that stalled for ten. That is the reported "lag",
           * and no server-side queue limit can reach a backlog that lives in
           * a socket buffer. Acknowledging after the draw means the agent
           * never gets more than a couple of frames ahead of what this
           * browser has actually painted.
           *
           * Sent even in view-only mode: pacing is not a change to the
           * remote machine.
           */
          const sock = ws;
          const ack = () => {
            try {
              if (sock && sock.readyState === WebSocket.OPEN) {
                sock.send(JSON.stringify({ t: "ack" }));
              }
            } catch {
              /* a closing socket is not worth reporting */
            }
          };
          createImageBitmap(blob)
            .then((bmp) => {
              const c = canvasRef.current;
              if (!c) return;
              if (c.width !== bmp.width || c.height !== bmp.height) {
                c.width = bmp.width;
                c.height = bmp.height;
              }
              const ctx = c.getContext("2d");
              ctx?.drawImage(bmp, 0, 0);
              bmp.close();
              setStatus("live");
              setStalled(false);
              attempt = 0; // a live frame resets the reconnect budget
              lastFrameRef.current = Date.now();
              wasLiveRef.current = true;
              liveOnThisSocketRef.current = true;
              frameCount.current++;
            })
            // Acknowledge even a frame we failed to decode: a corrupt or
            // dropped bitmap must not stall the stream for ever.
            .catch(() => {})
            .finally(ack);
        };
        ws.onclose = (e) => {
          if (cancelled) return;
          // A real agent-reported error (e.g. "no interactive session") is
          // terminal — don't fight it with reconnects.
          if (errRef.current) {
            setStatus("error");
            setStatusMsg(errRef.current);
            return;
          }
          // SAY WHEN A RESTART ENDED IT, rather than "Reconnecting…" while
          // the operator wonders about the network.
          //
          // Measured over fourteen days: 209 of 387 console sessions ended
          // with the error the server writes itself - "orphaned: backend
          // restarted while the session was open". The cause was recorded
          // every time and shown to nobody, so a restart looked like a
          // flaky tunnel and "the console keeps dropping" became received
          // wisdom about the network. Ask, then say.
          const tokenAtClose = activeTokenRef.current;
          if (tokenAtClose) {
            api
              .get(`/api/v1/console/screenshare/${tokenAtClose}/state`)
              .then((r) => {
                if (cancelled || !r.data?.by_restart) return;
                setStatusMsg(r.data.reason);
              })
              .catch(() => {
                /* best effort — a failed diagnosis must not break reconnect */
              });
          }
          // The worker dies when the user signs in (the login session is torn
          // down) or on transient drops. Auto-reopen a fresh session so the
          // now-logged-in desktop reappears instead of a dead disconnect.
          if (attempt < MAX_ATTEMPTS) {
            attempt++;
            setStatus("connecting");
            setStatusMsg(`Reconnecting… (${attempt})`);
            window.setTimeout(() => {
              if (!cancelled) connect(true);
            }, retryDelay(attempt));
            return;
          }
          setStatus("closed");
          setStatusMsg(
            e.reason === "agent_did_not_connect"
              ? "The device didn't connect — it may be offline or on an agent older than 1.0.58."
              : "Session ended.",
          );
        };
        ws.onerror = () => {
          /* onclose handles reconnect/teardown */
        };
      } catch (e: any) {
        if (cancelled) return;
        if (attempt < MAX_ATTEMPTS) {
          attempt++;
          window.setTimeout(() => {
            if (!cancelled) connect(true);
          }, retryDelay(attempt));
          return;
        }
        setStatus("error");
        setStatusMsg(e.response?.data?.detail || "Could not start the screen-share session.");
      }
    };

    connect(false);
    // Frame-staleness watchdog. When the worker dies at sign-in the browser WS
    // stays open (the broker parks it for the service-respawned worker), so
    // frames just freeze briefly then resume — seamless. If they don't resume,
    // surface "Reconnecting…" and finally force a full reconnect as a fallback.
    //
    // TWO CLOCKS, because "the picture froze" and "it has not started yet"
    // are different problems with very different budgets.
    //
    // A STREAM THAT WAS FLOWING and stops is a real fault, and 14s is the
    // right patience. A SOCKET THAT HAS NEVER DELIVERED A FRAME is usually
    // just waiting: the session is queued as an agent command, the agent
    // polls every 20s when idle, then spawns a worker in the console session
    // and dials back. Measured on a live device: 30s for one session, then
    // 70s for the next.
    //
    // Applying the 14s rule to that wait is what turned one drop into five
    // sessions in a minute - each retry minted a NEW session, the agent
    // eventually claimed them all at once, and the broker kept the newest
    // and closed the rest on arrival. The fix is not a longer single
    // timeout; it is noticing which of the two situations this is.
    const watchdog = window.setInterval(() => {
      if (cancelled) return;
      if (!liveOnThisSocketRef.current) {
        // Still waiting for the device. Only give up well past the agent's
        // worst case, and say what is being waited on rather than
        // "Reconnecting…", which implies something broke.
        const waiting = Date.now() - socketOpenedAtRef.current;
        if (waiting > 20000 && waiting <= CONNECT_BUDGET_MS) {
          setStatusMsg("Waiting for the device to connect…");
        } else if (waiting > CONNECT_BUDGET_MS && wsRef.current) {
          socketOpenedAtRef.current = Date.now(); // avoid a tight loop
          try {
            wsRef.current.close(); // -> onclose -> backed-off retry
          } catch {
            /* ignore */
          }
        }
        return;
      }
      const since = Date.now() - lastFrameRef.current;
      if (since > 4000 && since <= 14000) {
        setStatusMsg("Reconnecting…");
      } else if (since > 14000 && wsRef.current) {
        lastFrameRef.current = Date.now(); // avoid a tight loop
        try {
          wsRef.current.close(); // triggers onclose -> fresh session reconnect
        } catch {
          /* ignore */
        }
      }
    }, 1000);
    return () => {
      cancelled = true;
      window.clearInterval(watchdog);
      try {
        ws?.close();
      } catch {
        /* ignore */
      }
    };
    // adoptRemoteClip is useCallback([]) and so has a stable identity - it
    // can be declared honestly here without ever re-running this effect and
    // tearing down a live connection.
  }, [deviceId, presetToken, adoptRemoteClip]);

  // FPS meter.
  useEffect(() => {
    const t = setInterval(() => {
      setFps(frameCount.current);
      frameCount.current = 0;
    }, 1000);
    return () => clearInterval(t);
  }, []);

  // Keyboard: capture at the overlay level while focused.
  const onKey = useCallback((down: boolean) => (e: React.KeyboardEvent) => {
    if (!controlRef.current) return;
    e.preventDefault();
    // Ctrl+V is handled here instead of being forwarded: the remote machine
    // would otherwise paste ITS clipboard, not the one the operator just
    // copied from locally, which looks like the paste "not working".
    if (down && (e.ctrlKey || e.metaKey) && (e.key === "v" || e.key === "V")) {
      void pasteLocalToRemote();
      return;
    }
    const a = down ? "down" : "up";
    if (e.key.length === 1 && !e.ctrlKey && !e.altKey && !e.metaKey) {
      send({ t: "k", u: e.key, a });
    } else {
      const vk = SPECIAL_VK[e.key] ?? e.keyCode;
      if (vk) send({ t: "k", vk, a });
    }
  }, [pasteLocalToRemote, send]);

  const btn = (b: number) => (b === 2 ? 2 : b === 1 ? 1 : 0);

  return (
    <div ref={rootRef} className="fixed inset-0 z-[60] bg-black/90 flex flex-col" tabIndex={0}
      onKeyDown={onKey(true)} onKeyUp={onKey(false)}>
      <div className="flex items-center justify-between px-4 py-2 bg-gray-900 text-gray-100 text-sm gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <span className="font-semibold whitespace-nowrap">🖥 {hostname}</span>
          <span className={`text-xs px-2 py-0.5 rounded-full ${status === "live" ? "bg-green-600" : status === "connecting" ? "bg-amber-600" : "bg-gray-600"}`}>
            {status === "live" ? `live · ${fps} fps` : status}
          </span>
          {statusMsg && <span className="text-xs text-gray-400 truncate max-w-[18rem]">{statusMsg}</span>}
        </div>
        {/* Watching somebody else's session: say so, unmistakably. The view
            is identical to the operator's, so without this a watcher could
            believe they are driving and wonder why nothing responds. */}
        {observing && (
          <span className="text-[11px] px-2 py-1 rounded bg-indigo-600 whitespace-nowrap">
            👁 Watching — read-only
          </span>
        )}
        {/* Macro bar — send key combos the OS won't let SendInput do (Ctrl+Alt+Del). */}
        {control && status === "live" && (
          <div className="flex items-center gap-1">
            {activeToken && (
              <ConsoleInvitePanel kind="screenshare" sessionToken={activeToken} compact />
            )}
            <button
              onClick={() => {
                setClipOpen((v) => !v);
                setClipNote("");
              }}
              title="Copy and paste text between this computer and the remote one"
              className={`text-[11px] px-2 py-1 rounded whitespace-nowrap ${
                clipOpen ? "bg-blue-600 hover:bg-blue-500" : "bg-gray-700 hover:bg-gray-600"
              }`}
            >
              📋 Clipboard
            </button>
            {MACROS.map((m) => (
              <button key={m.label} onClick={m.run} title={m.title}
                className="text-[11px] bg-gray-700 hover:bg-gray-600 px-2 py-1 rounded whitespace-nowrap">
                {m.label}
              </button>
            ))}
          </div>
        )}
        <div className="flex items-center gap-3">
          {monCount > 1 && (
            <select
              value={selMon}
              onChange={(e) => switchMonitor(Number(e.target.value))}
              className="text-xs bg-gray-800 border border-gray-600 rounded px-2 py-1"
              title="Which of the device's monitors to view"
            >
              {monitors
                .filter((m) => m.i >= 1)
                .map((m) => (
                  <option key={m.i} value={m.i}>
                    Monitor {m.i} ({m.w}×{m.h})
                  </option>
                ))}
              <option value={0}>All monitors</option>
            </select>
          )}
          <label className="flex items-center gap-1.5 text-xs cursor-pointer whitespace-nowrap">
            <input type="checkbox" checked={control} onChange={(e) => setControl(e.target.checked)} />
            Control
          </label>
          <button onClick={toggleFullscreen} title="Fullscreen"
            className="text-xs bg-gray-700 hover:bg-gray-600 px-2 py-1 rounded">⛶</button>
          <button onClick={onClose} className="text-xs bg-rose-600 hover:bg-rose-700 px-3 py-1 rounded whitespace-nowrap">
            End ✕
          </button>
        </div>
      </div>

      {/* Clipboard bridge. Sync is automatic now, so this panel is the
          fallback and the explanation: it is where a refused browser
          permission gets named, and the only place to SEE what crossed. */}
      {clipOpen && status === "live" && (
        <div className="bg-gray-800 border-b border-gray-700 px-4 py-3 text-gray-100">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold">Clipboard</span>
            <span className="text-[11px] text-gray-400">
              {!control
                ? `View-only, so ${hostname}'s clipboard can be read but not changed.`
                : autoClip === "blocked"
                  ? "Automatic sync is off — this browser will not let the page read your clipboard."
                  : `Copying on either machine syncs automatically. Ctrl+V also pastes onto ${hostname}.`}
            </span>
          </div>
          {autoClip === "blocked" && control && (
            /* A permission prompt can only be raised from a real gesture,
               so the enable path has to be a button the operator clicks -
               there is no way to ask for it from a background timer. */
            <button
              onClick={async () => {
                setAutoClip("probing");
                const ok = await pushLocalClip();
                setClipNote(
                  ok
                    ? "Automatic clipboard sync is on."
                    : "The browser refused. Use the box below, or allow clipboard access for this site in the address bar.",
                );
              }}
              className="mb-2 text-[11px] bg-blue-600 hover:bg-blue-500 px-2.5 py-1 rounded"
            >
              Turn on automatic sync
            </button>
          )}
          <textarea
            value={clipText}
            onChange={(e) => setClipText(e.target.value)}
            // Stop keystrokes in the box being forwarded to the remote machine.
            onKeyDown={(e) => e.stopPropagation()}
            onKeyUp={(e) => e.stopPropagation()}
            rows={3}
            placeholder="Text to send to the remote machine, or what came back from it."
            className="w-full text-xs font-mono bg-gray-900 border border-gray-600 rounded-lg px-3 py-2 text-gray-100"
          />
          <div className="flex flex-wrap items-center gap-2 mt-2">
            <button
              onClick={() => sendClipboardToRemote(clipText)}
              disabled={!control || !clipText}
              title={control ? undefined : "This session is view-only"}
              className="text-[11px] bg-blue-600 hover:bg-blue-500 disabled:opacity-40 px-2.5 py-1 rounded"
            >
              Send to {hostname}
            </button>
            <button
              onClick={fetchRemoteClipboard}
              className="text-[11px] bg-gray-700 hover:bg-gray-600 px-2.5 py-1 rounded"
            >
              Get from {hostname}
            </button>
            <button
              onClick={copyFetchedToLocal}
              disabled={!clipText}
              className="text-[11px] bg-gray-700 hover:bg-gray-600 disabled:opacity-40 px-2.5 py-1 rounded"
            >
              Copy to this computer
            </button>
            {clipNote && <span className="text-[11px] text-gray-400">{clipNote}</span>}
          </div>
        </div>
      )}

      <div className="flex-1 flex items-center justify-center overflow-hidden p-2 relative">
        {status !== "live" && (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6 pointer-events-none">
            <div className={`text-lg font-medium ${status === "error" ? "text-rose-300" : "text-gray-300"}`}>
              {status === "connecting" ? "Connecting…" : status === "error" ? "⚠ Screen share unavailable" : "Session ended"}
            </div>
            {statusMsg && <div className="text-sm text-gray-400 mt-2 max-w-lg">{statusMsg}</div>}
            {stalled && status === "connecting" && (
              // Say what is probably wrong AND what to do instead. A spinner
              // that never resolves teaches people the feature is broken;
              // this at least hands them the next move.
              <div className="mt-4 max-w-lg text-sm text-amber-300/90 bg-amber-500/10 border border-amber-500/30 rounded-lg px-4 py-3 pointer-events-auto">
                <div className="font-medium mb-1">Still nothing from {hostname}.</div>
                <div className="text-amber-200/80">
                  The most common reason on a server is that nobody is signed in, so there
                  is no desktop to show — Remote Desktop works in that case. It can also
                  mean the agent could not start its helper.
                </div>
              </div>
            )}
          </div>
        )}
        <canvas
          ref={canvasRef}
          /* Show the local pointer over the canvas: mss does not capture the
             remote cursor (Windows draws it as a hardware overlay), so hiding
             the local one left no pointer at all. It maps 1:1 to where clicks
             land, so it's an accurate, zero-latency pointer. */
          className={`max-w-full max-h-full object-contain cursor-default bg-black`}
          onMouseMove={(e) => control && send({ t: "m", ...norm(e), a: "move" })}
          onMouseDown={(e) => control && send({ t: "m", ...norm(e), b: btn(e.button), a: "down" })}
          onMouseUp={(e) => control && send({ t: "m", ...norm(e), b: btn(e.button), a: "up" })}
          onWheel={(e) => control && send({ t: "scroll", d: e.deltaY > 0 ? -1 : 1 })}
          onContextMenu={(e) => e.preventDefault()}
        />
      </div>
    </div>
  );
}
